import { createSlice } from '@reduxjs/toolkit'
import { NameDto } from 'api/Common/Types'
import { AccountGroupBasicDto, AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { AccountType } from 'api/Trading-Accounts/Types/AccountType'
import { BookType } from 'api/Trading-Accounts/Types/BookType'
import { CampaignTypeDto } from 'api/Trading-Accounts/Types/CampaignTypeDto'
import { CountryOverridesDtoResponse } from 'api/Trading-Accounts/Types/CountryOverridesType'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { LeverageType } from 'api/Trading-Accounts/Types/LeverageType'
import { PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { ServerType } from 'api/Trading-Accounts/Types/ServerType'
import { SubscriptionGroupDto } from 'api/Trading-Accounts/Types/SubscriptionGroupDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'

const initialState: TradingAccountState = {
  bookType: [],
  tradingAccountStatus: [],
  tradingAccountType: [],
  tradingAccountClassificationType: [],
  tradingAccountInstrumentType: [],
  tradingAccountGroup: [],
  tradingAccountLiveGroup: [],
  tradingAccountDemoGroup: [],
  tradingAccountWelcomeGroup: [],
  tradingAccountGroupType: [],
  tradingAccountGroupsByTradingAccount: [],
  tradingAccountSubscriptionClassificationTypes: [],
  tradingAccountSubscriptionDetailTypes: [],
  leverages: [],
  platformTypes: [],
  platformServers: [],
  allServers: [],
  instruments: [],
  restrictions: [],
  accountGroupsByDemoAccount: [],
  accountGroupsByWelcomeAccount: [],
  currencies: [],
  campaignTypes: [],
  campaignCalculationTypes: [],
  campaignStatuses: [],
  campaignBusinessRuleTypes: [],
  intervalTypes: [],
  cashbackFrequencies: [],
  cashbackAmountTypes: [],
  cashbackStates: [],
  subscriptionGroups: [],
  billingPeriods: [],
  dividendSubTypes: [],
  corporateActionTypes: [],
  clientPlatformType: [],
  extendedTradingAccountType: [],
  leverageForPlatforms: [],
  operatingSystems: [],
  ibRebatesApprovalTypes: [],
  ibRebatesPeriods: [],
  countryOverrides: [],
  marginBonusResultStatus: [],
  rewardCategories: [],
  rewardTrackingMetrics: [],
  rewardProgramStatuses: [],
  rewardProgramTierConditionsLogic: [],
  rewardProgramResetCadence: [],
  rewardProgramTierLogicalOperators: [],
}

const sortInstrumentsByDisplayName = (instruments: InstrumentDto[]): InstrumentDto[] =>
  [...instruments].sort((a, b) =>
    a.symbol.localeCompare(b.symbol, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  )

export interface TradingAccountState {
  bookType: BookType[]
  tradingAccountStatus: NameDto[]
  tradingAccountType: AccountType[]
  tradingAccountClassificationType: NameDto[]
  tradingAccountInstrumentType: NameDto[]
  tradingAccountGroup: AccountGroupDto[]
  tradingAccountLiveGroup: AccountGroupDto[]
  tradingAccountDemoGroup: AccountGroupDto[]
  tradingAccountWelcomeGroup: AccountGroupDto[]
  tradingAccountGroupType: NameDto[]
  tradingAccountGroupsByTradingAccount: AccountGroupBasicDto[]
  tradingAccountSubscriptionClassificationTypes: NameDto[]
  tradingAccountSubscriptionDetailTypes: NameDto[]
  leverages: LeverageType[]
  platformTypes: PlatformTypeDto[]
  platformServers: ServerType[]
  allServers: ServerType[]
  instruments: InstrumentDto[]
  restrictions: NameDto[]
  accountGroupsByDemoAccount: AccountGroupBasicDto[]
  accountGroupsByWelcomeAccount: AccountGroupBasicDto[]
  currencies: WalletCurrencyDto[]
  campaignTypes: CampaignTypeDto[]
  campaignCalculationTypes: NameDto[]
  campaignStatuses: NameDto[]
  campaignBusinessRuleTypes: NameDto[]
  intervalTypes: NameDto[]
  cashbackFrequencies: NameDto[]
  cashbackAmountTypes: NameDto[]
  cashbackStates: NameDto[]
  subscriptionGroups: SubscriptionGroupDto[]
  billingPeriods: NameDto[]
  dividendSubTypes: NameDto[]
  corporateActionTypes: NameDto[]
  clientPlatformType: NameDto[]
  extendedTradingAccountType: NameDto[]
  leverageForPlatforms: LeverageType[]
  operatingSystems: NameDto[]
  ibRebatesApprovalTypes: NameDto[]
  ibRebatesPeriods: NameDto[]
  countryOverrides: CountryOverridesDtoResponse[]
  marginBonusResultStatus: NameDto[]
  rewardCategories: NameDto[]
  rewardTrackingMetrics: NameDto[]
  rewardProgramStatuses: NameDto[]
  rewardProgramTierConditionsLogic: NameDto[]
  rewardProgramResetCadence: NameDto[]
  rewardProgramTierLogicalOperators: NameDto[]
}

export const tradingAccountsSlice = createSlice({
  name: 'tradingAccounts',
  initialState,
  reducers: {
    setBookType: (state, action) => {
      state.bookType = action.payload
    },
    setTradingAccountPlatformType: (state, action) => {
      state.clientPlatformType = action.payload
    },
    setExtendedTradingAccountType: (state, action) => {
      state.extendedTradingAccountType = action.payload
    },
    setTradingAccountLeverageTypesForPlatforms: (state, action) => {
      state.leverageForPlatforms = action.payload
    },
    setLeverageTypes: (state, action) => {
      state.leverages = action.payload
    },
    setServers: (state, action) => {
      state.platformServers = action.payload
    },
    setAllServers: (state, action) => {
      state.allServers = action.payload
    },
    setPlatformTypes: (state, action) => {
      state.platformTypes = action.payload
    },
    setTradingAccountStatus: (state, action) => {
      state.tradingAccountStatus = action.payload
    },
    setTradingAccountType: (state, action) => {
      state.tradingAccountType = action.payload
    },
    setTradingAccountClassificationType: (state, action) => {
      state.tradingAccountClassificationType = action.payload
    },
    setTradingAccountInstrumentType: (state, action) => {
      state.tradingAccountInstrumentType = action.payload
    },
    setTradingAccountGroups: (state, action) => {
      state.tradingAccountGroup = action.payload
    },
    setLiveTradingAccountGroups: (state, action) => {
      state.tradingAccountLiveGroup = action.payload
    },
    setDemoTradingAccountGroups: (state, action) => {
      state.tradingAccountDemoGroup = action.payload
    },
    setWelcomeTradingAccountGroups: (state, action) => {
      state.tradingAccountWelcomeGroup = action.payload
    },
    setTradingAccountGroupType: (state, action) => {
      state.tradingAccountGroupType = action.payload
    },
    setTradingAccountGroupsBy: (state, action) => {
      state.tradingAccountGroupsByTradingAccount = action.payload
    },
    setInstruments: (state, action) => {
      state.instruments = sortInstrumentsByDisplayName(action.payload)
    },
    setRestrictionTypes: (state, action) => {
      state.restrictions = action.payload
    },
    setAccountGroupsByDemoAccount: (state, action) => {
      state.accountGroupsByDemoAccount = action.payload
    },
    setAccountGroupsByWelcomeAccount: (state, action) => {
      state.accountGroupsByWelcomeAccount = action.payload
    },
    setTradingAccountCurrencies: (state, action) => {
      state.currencies = action.payload
    },
    setCampaignTypes: (state, action) => {
      state.campaignTypes = action.payload
    },
    setCampaignCalculationTypes: (state, action) => {
      state.campaignCalculationTypes = action.payload
    },
    setCampaignStatuses: (state, action) => {
      state.campaignStatuses = action.payload
    },
    setCampaignBusinessRuleTypes: (state, action) => {
      state.campaignBusinessRuleTypes = action.payload
    },
    setIntervalTypes: (state, action) => {
      state.intervalTypes = action.payload
    },
    setIntroducingBrokerCashbackFrequencies: (state, action) => {
      state.cashbackFrequencies = action.payload
    },
    setIntroducingBrokerCashbackAmountTypes: (state, action) => {
      state.cashbackAmountTypes = action.payload
    },
    setIntroducingBrokerCashbackStates: (state, action) => {
      state.cashbackStates = action.payload
    },
    setTradingAccountSubscriptionClassificationTypes: (state, action) => {
      state.tradingAccountSubscriptionClassificationTypes = action.payload
    },
    setTradingAccountSubscriptionDetailTypes: (state, action) => {
      state.tradingAccountSubscriptionDetailTypes = action.payload
    },
    setTradingAccountSubscriptionGroups: (state, action) => {
      state.subscriptionGroups = action.payload
    },
    setTradingAccountSubscriptionBillingPeriods: (state, action) => {
      state.billingPeriods = action.payload
    },
    setDividendSubTypes: (state, action) => {
      state.dividendSubTypes = action.payload
    },
    setCorporateActionTypes: (state, action) => {
      state.corporateActionTypes = action.payload
    },
    setTradingAccountGroupsByTradingAccount: (state, action) => {
      state.tradingAccountGroupsByTradingAccount = action.payload
    },
    setOperatingSystems: (state, action) => {
      state.operatingSystems = action.payload
    },
    setIBRebatesApprovalTypes: (state, action) => {
      state.ibRebatesApprovalTypes = action.payload
    },
    setIBRebatesPeriods: (state, action) => {
      state.ibRebatesPeriods = action.payload
    },
    setCountryOverrides: (state, action) => {
      state.countryOverrides = action.payload
    },
    setMarginBonusResultStatus: (state, action) => {
      state.marginBonusResultStatus = action.payload
    },
    setRewardCategories: (state, action) => {
      state.rewardCategories = action.payload
    },
    setRewardTrackingMetrics: (state, action) => {
      state.rewardTrackingMetrics = action.payload
    },
    setRewardProgramStatuses: (state, action) => {
      state.rewardProgramStatuses = action.payload
    },
    setRewardProgramTierConditionsLogic: (state, action) => {
      state.rewardProgramTierConditionsLogic = action.payload
    },
    setRewardProgramResetCadence: (state, action) => {
      state.rewardProgramResetCadence = action.payload
    },
    setRewardProgramTierLogicalOperators: (state, action) => {
      state.rewardProgramTierLogicalOperators = action.payload
    },
  },
})
